import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Link } from 'react-router-dom';
import Drawer from '~/components/Drawer';
import { sectionStyles } from './styles';
import PersonDetails from './PersonDetails';
import RelationshipAndRole from './TeamDrawer/RelationshipAndRole';
import { createTeamMember, putTeamMember } from '~/services/campaign';
import PasswordManagement from './PasswordManagement';
import toast from 'react-hot-toast';

const roleOptions = [
    {
        label: 'Limited Access',
        value: 'limited',
        description:
            'The team member can edit all the items on the Campaign Page except the goal and show fundraising thermometer',
    },
    {
        label: 'Full Access',
        value: 'full',
        description:
            "In addition to editing all the items on the Campaign Page, the team member can also: access the list of contributors, add or change team members, change the client's contact information, add the client to the Find a Friend Network.",
    },
    {
        label: 'No Login',
        value: 'no',
        description:
            'The team member may view the public Campaign Page, but does not have a login to edit the website or other client information',
    },
];

// Helper function to format phone numbers
const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');
    
    // Only format if we have enough digits (at least 10)
    if (digits.length < 10) return phone; // Return as-is if incomplete
    
    // Format as (XXX) XXX-XXXX
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

interface Props {
    isOpen: boolean;
    setIsOpen: any;
    isAdd: boolean;
    teamMember: any;
    metadata: any;
    campaign: any;
    onDataUpdate?: () => void;
    campaignDispatch?: any;
}

const TeamDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    isAdd,
    teamMember,
    metadata,
    campaign,
    onDataUpdate,
    campaignDispatch,
}) => {

    const methods = useForm({
        defaultValues: {
            id: '',
            person: {
                title: '',
                suffix: '',
                phone_set: [{ phone: '', type: 'Cell' }],
            },
            relationship_to_patient_id: null,
            role: '',
            is_primary_contact: false,
            is_emergency_contact: false,
            can_discuss_balance: false,
            can_request_disbursement: false,
            notes: '',
        },
    });

    const { reset } = methods;

    useEffect(() => {
        // Only reset when drawer is opened
        if (!isOpen) return;
        
        // If adding a new team member, reset to clean defaults
        if (isAdd || !teamMember) {
            reset({
                id: '',
                person: {
                    title: '',
                    first_name: '',
                    middle_name: '',
                    last_name: '',
                    suffix: '',
                    address_1: '',
                    address_2: '',
                    city: '',
                    state: '',
                    zip_code: '',
                    email: '',
                    nicknames: '',
                    phone_set: [{ phone: '', type: 'Cell' }],
                },
                relationship_to_patient_id: null,
                role: '',
                is_primary_contact: teamMember?.is_primary_contact ?? false,
                is_emergency_contact: teamMember?.is_emergency_contact ?? false,
                can_discuss_balance: false,
                can_request_disbursement: false,
                notes: '',
            });
            return;
        }
        
        // Editing existing team member - normalize access value to role value
        let normalizedRole = '';
        if (teamMember?.access) {
            const accessLower = teamMember.access.toLowerCase().trim();
            if (accessLower.includes('full')) {
                normalizedRole = 'full';
            } else if (accessLower.includes('limited')) {
                normalizedRole = 'limited';
            } else if (accessLower.includes('no')) {
                normalizedRole = 'no';
            } else {
                normalizedRole = teamMember.access;
            }
        }
        
        // Convert phone_set from backend format to form format and format phone numbers
        let phoneSet = [{ phone: '', type: 'Cell' }];
        if (teamMember?.person?.phone_set && teamMember.person.phone_set.length > 0) {
            phoneSet = teamMember.person.phone_set.map(p => ({
                ...p,
                phone: formatPhoneNumber(p.phone || '')
            }));
        } else if (teamMember?.person?.phone) {
            // Handle legacy single phone field
            phoneSet = [{ phone: formatPhoneNumber(teamMember.person.phone), type: 'Cell' }];
        }
        
        reset({
            id: teamMember?.id,
            person: {
                title: teamMember.person?.title || '',
                first_name: teamMember.person?.first_name || '',
                middle_name: teamMember.person?.middle_name || '',
                last_name: teamMember.person?.last_name || '',
                suffix: teamMember.person?.suffix || '',
                address_1: teamMember.person?.address_1 || '',
                address_2: teamMember.person?.address_2 || '',
                city: teamMember.person?.city || '',
                state: teamMember.person?.state || '',
                zip_code: teamMember.person?.zip_code || '',
                email: teamMember.person?.email || '',
                nicknames: teamMember.person?.nicknames || '',
                phone_set: phoneSet,
            },
            relationship_to_patient_id: teamMember.relationship_to_patient
                ? {
                      value: teamMember.relationship_to_patient.id,
                      label: teamMember.relationship_to_patient.name,
                  }
                : null,
            role: normalizedRole,
            is_primary_contact: teamMember.is_primary_contact ?? false,
            is_emergency_contact: teamMember.is_emergency_contact ?? false,
            can_discuss_balance: teamMember.can_discuss_balance ?? false,
            can_request_disbursement: teamMember.can_request_disbursement ?? false,
            notes: teamMember.notes || '',
        });
    }, [teamMember, reset, isOpen, isAdd]);

    const [errMsg, setErrMsg] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Clear error message and reset saving state when drawer opens/closes
    useEffect(() => {
        if (!isOpen) {
            setErrMsg('');
            setIsSaving(false);
        }
    }, [isOpen]);

    type PersonForm = {
        title?: string;
        first_name: string;
        middle_name: string;
        last_name: string;
        suffix?: string;
        address_1: string;
        address_2: string;
        city: string;
        state: string;
        zip_code: string;
        email: string;
        nicknames?: string;
        phone_set: { phone: string; type: string; id?: number }[];
    };

    type TeamMemberForm = {
        id?: string;
        campaign_id: string;
        person: PersonForm;
        relationship_to_patient_id:
            | {
                  value: number;
                  label: string;
              }
            | number;
        role: string;
        is_primary_contact: boolean;
        is_emergency_contact: boolean;
        can_discuss_balance: boolean;
        can_request_disbursement: boolean;
    };

    const onSubmit = async (data: TeamMemberForm) => {
        const {
            person: { phone_set, email },
        } = data;

        // Check if there's at least one non-empty phone number
        const hasPhone = phone_set && phone_set.length > 0 && phone_set[0].phone?.trim();
        
        if (!hasPhone && !email) {
            setErrMsg('Phone number or email address is required.');
            return;
        }

        if (data.role === 'full' && !email) {
            setErrMsg(
                'Team members with full access must provide an email address'
            );
            return;
        }
        
        // Validate relationship_to_patient_id
        if (!data.relationship_to_patient_id || !data.relationship_to_patient_id?.value) {
            setErrMsg('Relationship to Client is required.');
            return;
        }
        
        // Validate role
        if (!data.role) {
            setErrMsg('Role is required.');
            return;
        }
        
        setErrMsg('');
        
        // Filter out empty phone entries
        if (data.person.phone_set) {
            data.person.phone_set = data.person.phone_set.filter(p => p.phone?.trim() !== '');
        }

        setIsSaving(true);

        try {
            let resp;
            data.relationship_to_patient_id =
                data.relationship_to_patient_id.value;
            
            // Handle both legacy campaigns (campaign_id) and new applications (id)
            const campaignId = campaign?.campaign_id || campaign?.id;
            
            if (!campaignId) {
                setErrMsg('Campaign ID is missing. Please refresh and try again.');
                setIsSaving(false);
                return;
            }
            
            const payload: TeamMemberForm = {
                ...data,
                campaign_id: campaignId,
            };

            if (isAdd) {
                resp = await createTeamMember(payload);
            } else {
                resp = await putTeamMember(payload);
            }

            if (resp.detail) {
                setErrMsg(resp.detail);
                toast.error(resp.detail);
            } else {
                toast.success(`Team member ${isAdd ? 'added' : 'updated'} successfully`);
                
                // Update team member in context instead of refetching
                if (campaignDispatch) {
                    if (isAdd) {
                        campaignDispatch({
                            type: 'add_team_member',
                            payload: resp,
                        });
                    } else {
                        campaignDispatch({
                            type: 'update_team_member',
                            payload: resp,
                        });
                    }
                } else if (onDataUpdate) {
                    // Fallback to full refresh if campaignDispatch not provided
                    onDataUpdate();
                }
                
                setIsOpen(false);
            }
        } catch (e) {
            console.error('Error saving team member:', e);
            const errorMessage = e?.message || 'Failed to save team member. Please try again.';
            setErrMsg(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDrawerClose = () => {
        if (!isSaving) {
            setIsOpen(false);
        }
    };

    const drawerTitle = (
        <span className='inline-flex items-center gap-2'>
            {`${isAdd ? 'Add' : 'Edit'} Team Member`}
            {!isAdd && teamMember?.person?.user && (
                <Link
                    to={`/users/${teamMember.person.user}`}
                    className='text-primary hover:opacity-70'
                    title='View User Profile'
                >
                    <svg
                        xmlns='http://www.w3.org/2000/svg'
                        className='h-5 w-5'
                        fill='none'
                        viewBox='0 0 24 24'
                        stroke='currentColor'
                        strokeWidth={2}
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                        />
                    </svg>
                </Link>
            )}
        </span>
    );

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={handleDrawerClose}
            title={drawerTitle}
        >
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <h2 className={sectionStyles}>CONTACT INFORMATION</h2>
                    <PersonDetails
                        multiplePhoneNumbers={true}
                        states={metadata?.states}
                    />
                    <h2 className={sectionStyles}>RELATIONSHIP AND ROLE</h2>
                    <RelationshipAndRole
                        teamMember={teamMember}
                        roleOptions={roleOptions}
                        metadata={metadata}
                    />
                    {!isAdd && teamMember?.person?.user && teamMember?.person?.email && (
                        <PasswordManagement
                            userId={teamMember.person.user}
                            userName={`${teamMember.person.first_name} ${teamMember.person.last_name}`}
                            onPasswordChanged={onDataUpdate}
                        />
                    )}
                    <p style={{ color: '#ff4b4b' }} className='mt-1 text-sm'>
                        {errMsg}
                    </p>
                    {!isAdd && (
                        <button 
                            type='button'
                            className='hover: mt-[30px] w-full grow rounded-full border-[2px] border-[#F02D3A] bg-[#F02D3A] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] hover:text-black disabled:cursor-not-allowed disabled:opacity-50'
                            disabled={isSaving}
                        >
                            Delete
                        </button>
                    )}
                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={handleDrawerClose}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default TeamDrawer;
